import { useUserStore } from '@/store/modules/user'
import {
    getAccessToken,
    getRefreshToken,
    setToken,
    removeToken
} from '@/services/utils/auth'
import errorCode from '@/services/utils/errorCode'
import { toast, tansParams } from '@/services/utils/common'
import { initializeLoginTenantScope, loginTenantScope, requireLoginTenant } from './tenantScope'
import { openAuthenticationGateway } from '@/services/collaboration-login/navigation'
import { authenticationState, markAuthenticationReady, waitForAuthentication } from '@/services/collaboration-login/auth-state'

/**
 * 请求配置类型
 */
interface RequestOptions extends Partial<UniApp.RequestOptions> {
    url: string
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    params?: Record<string, any>
    data?: Record<string, any>
    headers?: Record<string, any>
    baseUrl?: string
    loading?: boolean
    silent?: boolean
    authRetried?: boolean
    authBootstrap?: boolean
    authReplay?: boolean
}

const timeout = 10000
const baseUrl =
    import.meta.env.VITE_APP_API_BASEURL + import.meta.env.VITE_APP_PROXY_PREFIX
// 本地联调时可通过 VITE_REQUEST_TAG 给测试环境网关传递灰度标签，
// 需要与本地后端服务配置的 sys.env.tag 保持一致。
const requestTag = String(import.meta.env.VITE_REQUEST_TAG || '').trim()

let tokenRefresh: Promise<void> | undefined
let tokenRefreshGeneration = -1
let isHandlingLoginExpired = false
let loginExpiredPromise: Promise<void> | null = null

const sessionExpiredError = {
    code: 401,
    msg: '无效的会话，或者会话已过期，请重新登录。'
}

/**
 * 获取当前页面路径
 */
export const getCurrentPagesUrl = (): string => {
    const pages = getCurrentPages()
    const currentPage = pages[pages.length - 1]
    return currentPage?.route || currentPage?.__route__ || '/'
}

/**
 * 兼容旧调用方，统一认证不再弹出登录过期确认框。
 */
export const shouldShowTokenExpireConfirm = (): boolean => {
    return false
}

/**
 * 将失效或暂时不可恢复的会话交给统一认证页。
 * 首个参数保留旧调用签名；临时故障不清理凭据。
 */
export const handleLoginExpired = async (_showConfirmBox = true, clearCredentials = true): Promise<void> => {
    if (isHandlingLoginExpired && loginExpiredPromise) return loginExpiredPromise

    isHandlingLoginExpired = true
    loginExpiredPromise = (async () => {
        const userStore = useUserStore()
        if (clearCredentials) removeToken()
        userStore.resetState()
        await openAuthenticationGateway()
    })().catch(error => {
        resetLoginExpiredHandling()
        throw error
    })
    return loginExpiredPromise
}

export const resetLoginExpiredHandling = () => {
    isHandlingLoginExpired = false
    loginExpiredPromise = null
}

/**
 * 刷新 token
 */
export const refreshToken = async (): Promise<UniApp.RequestSuccessCallbackResult> => {
    await initializeLoginTenantScope()
    const tenantId = requireLoginTenant()
    const header: Record<string, any> = {}
    if (loginTenantScope.enabled && tenantId) header['tenant-id'] = tenantId
    if (requestTag) {
        header.tag = requestTag
    }

    return (await uni.request({
        method: 'POST',
        timeout,
        url:
            baseUrl +
            '/system/auth/refresh-token?refreshToken=' +
            getRefreshToken(),
        header
    })) as unknown as UniApp.RequestSuccessCallbackResult
}

function refreshSession(): Promise<void> {
    const generation = authenticationState.generation
    if (tokenRefresh && tokenRefreshGeneration === generation) return tokenRefresh
    tokenRefreshGeneration = generation
    const task = (async () => {
        if (!getRefreshToken()) throw sessionExpiredError
        const wasReady = authenticationState.status === 'ready'
        authenticationState.status = 'refreshing'
        try {
            const response = await refreshToken()
            const result = response.data as any
            if ([400, 401].includes(result?.code)) throw sessionExpiredError
            if (!result || result.code !== 0 || !result.data?.accessToken || !result.data?.refreshToken) {
                throw { code: result?.code || 'INVALID_RESPONSE', msg: result?.msg || '登录续期失败，请重试' }
            }
            if (generation !== authenticationState.generation) throw sessionExpiredError
            setToken(result.data)
            if (wasReady) markAuthenticationReady()
        } catch (error) {
            if (generation === authenticationState.generation
                && (error as { code?: number })?.code !== 401) {
                authenticationState.status = wasReady ? 'ready' : 'error'
            }
            throw error
        }
    })().finally(() => { if (tokenRefresh === task) tokenRefresh = undefined })
    tokenRefresh = task
    return task
}

/**
 * 递归处理空字符串
 * @param input
 * @returns
 */
type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
interface JsonObject { [key: string]: JsonValue; }
interface JsonArray extends Array<JsonValue> { }
function deepReplaceEmptyString<T extends JsonValue>(input: T): T {
    if (input === '') return null as T;

    if (Array.isArray(input)) {
        return input.map(item => deepReplaceEmptyString(item)) as T;
    }

    if (typeof input === 'object' && input !== null) {
        const result: any = {};
        for (const key in input) {
            result[key] = deepReplaceEmptyString((input as JsonObject)[key]);
        }
        return result;
    }

    return input;
}

const buildUrlWithParams = (url: string, params?: Record<string, any>) => {
    if (!params) return url

    const query = tansParams(params).slice(0, -1)
    if (!query) return url

    const separator = url.includes('?')
        ? (url.endsWith('?') || url.endsWith('&') ? '' : '&')
        : '?'

    return url + separator + query
}

/**
 * 通用请求封装
 */
const request = async (config: RequestOptions): Promise<any> => {
    config = { ...config, header: { ...config?.header } }
    const loading = config?.loading || false
    const silent = config?.silent || false
    if (!config?.url || typeof config.url !== 'string') {
        const error = { code: 'INVALID_REQUEST_URL', msg: '请求地址未配置' }
        console.error('[request] invalid request url', config)
        return Promise.reject(error)
    }
    const isToken = (config.headers || {}).isToken === false
    const tenantLookup = config.url === '/system/tenant/get-id-by-name' && isToken
    if (!tenantLookup) await initializeLoginTenantScope()
    const tenantId = tenantLookup ? undefined : requireLoginTenant()
    if (!isToken && !config.authBootstrap && !config.authRetried) {
        const generation = authenticationState.generation
        const authenticated = await waitForAuthentication()
        if (generation !== authenticationState.generation && (getAccessToken() || getRefreshToken())) throw sessionExpiredError
        if (!authenticated) {
            await handleLoginExpired(false)
            throw sessionExpiredError
        }
    }

    config.header = config.header || {}

    // 添加 token
    if (getAccessToken() && !isToken) {
        config.header['Authorization'] = 'Bearer ' + getAccessToken()
    }

    if (loginTenantScope.enabled && tenantId) config.header['tenant-id'] = tenantId
    else delete config.header['tenant-id']
    // 仅在本地显式配置联调标签时追加，避免影响测试/正式环境默认路由。
    if (requestTag) {
        config.header.tag = requestTag
    }

    // 拼接 GET 参数
    const requestUrl = buildUrlWithParams(config.url, config.params)
    const sentToken = getAccessToken()
    const sentGeneration = authenticationState.generation

    return new Promise((resolve, reject) => {
        if (loading) uni.showLoading({ title: '加载中...', mask: true })

        uni.request({
            method: config.method || 'GET',
            timeout: config.timeout || timeout,
            url: config.baseUrl || baseUrl + requestUrl,
            data: deepReplaceEmptyString(config.data || {}),
            header: config.header,
            dataType: 'json',

            success: async (response: UniApp.RequestSuccessCallbackResult) => {
                try {
                    if (!isToken && sentGeneration !== authenticationState.generation) return reject(sessionExpiredError)
                    const resp = response.data as any
                    if (!resp || typeof resp.code !== 'number') {
                        return reject({ code: 'INVALID_RESPONSE', msg: '接口返回格式异常，请重试' })
                    }
                    const { code, msg } = resp

                    if (code === 401) {
                        // Never replay one-time authorization/login requests after refreshing a token.
                        if (isToken) return reject({ code, msg: msg || sessionExpiredError.msg })
                        if (config.authReplay === false) return reject(sessionExpiredError)
                        if (sentGeneration !== authenticationState.generation) return reject(sessionExpiredError)
                        if (isHandlingLoginExpired) {
                            return reject(sessionExpiredError)
                        }
                        if (config.authRetried) {
                            if (!config.authBootstrap) await handleLoginExpired(false)
                            return reject(sessionExpiredError)
                        }
                        try {
                            if (sentToken === getAccessToken()) await refreshSession()
                            return resolve(request({ ...config, authRetried: true }))
                        } catch (error) {
                            if (!config.authBootstrap && sentGeneration === authenticationState.generation
                                && (error as { code?: number })?.code === 401) await handleLoginExpired(false)
                            // Network/server failures do not invalidate otherwise recoverable credentials.
                            return reject(error)
                        }
                    } else if (code === 500) {
                        if (!silent) toast(msg || '服务器内部错误')
                        return reject({ code, msg: msg || errorCode['500'] })
                    } else if (code !== 0) {
                        if (!silent) toast(msg)
                        return reject({ code, msg })
                    }

                    resolve(resp)
                } catch (error) {
                    reject(error)
                }
            },

            fail: (error: any) => {
                let message = error?.message || error?.errMsg || '系统接口请求失败'
                if (message === 'Network Error') message = '后端接口连接异常'
                else if (message.includes('timeout')) message = '系统接口请求超时'
                else if (message.includes('Request failed with status code')) {
                    message = '系统接口' + message.slice(-3) + '异常'
                }
                if (!silent) toast(message)
                reject(error)
            },

            complete: () => {
                if (loading) {
                    setTimeout(() => uni.hideLoading(), 0)
                }
            }
        })
    })
}

/**
 * GET 请求封装
 */
export const httpGet = (
    url: string,
    params?: Record<string, any>
): Promise<any> => {
    return request({ url, params, method: 'GET' })
}

/**
 * POST 请求封装
 */
export const httpPost = (
    url: string,
    data?: Record<string, any>,
    params?: Record<string, any>
): Promise<any> => {
    return request({ url, data, params, method: 'POST' })
}

request.get = httpGet
request.post = httpPost

export default request
